import { useCallback, useRef, useState } from 'react';

import { getSnapshotChangeDetectionResource } from '../../../api/snapshotChangeDetection';
import { SnapshotComparisonDetailRow } from '../types';

const LETTER_LOAD_ERROR = 'Could not load letter.';

/** Fetches signed letter PDFs for the Letter modal via snapshot_change_detection/resource. */
const useSnapshotComparisonLetter = () => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SnapshotComparisonDetailRow | null>(null);
  const [letterIndex, setLetterIndex] = useState(0);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loadGenerationRef = useRef(0);

  const fetchSignedUrl = useCallback(async (s3Path: string, center: string, generation: number) => {
    setLoading(true);
    setPdfUrl('');
    setError('');

    try {
      const response = await getSnapshotChangeDetectionResource({ s3_path: s3Path, center });

      if (generation !== loadGenerationRef.current) {
        return;
      }

      if (response?.data?.status === 200 && response?.data?.body?.s3_url) {
        setPdfUrl(response.data.body.s3_url);
        return;
      }

      setError(LETTER_LOAD_ERROR);
    } catch {
      if (generation !== loadGenerationRef.current) {
        return;
      }

      setError(LETTER_LOAD_ERROR);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadLetterAtIndex = useCallback(
    (row: SnapshotComparisonDetailRow, index: number) => {
      const links = row.letter_links ?? [];
      const s3Path = links[index];

      if (!s3Path) {
        setError('No letter available.');
        setLoading(false);
        return;
      }

      if (!row.center) {
        setError('Missing center for this row.');
        setLoading(false);
        return;
      }

      loadGenerationRef.current += 1;
      const generation = loadGenerationRef.current;
      fetchSignedUrl(s3Path, row.center, generation);
    },
    [fetchSignedUrl]
  );

  const openLetter = useCallback(
    (row: SnapshotComparisonDetailRow) => {
      if (!row.letter_links?.length) {
        return;
      }

      setSelectedRow(row);
      setLetterIndex(0);
      setOpen(true);
      loadLetterAtIndex(row, 0);
    },
    [loadLetterAtIndex]
  );

  const closeLetter = useCallback(() => {
    loadGenerationRef.current += 1;
    setOpen(false);
    setSelectedRow(null);
    setLetterIndex(0);
    setPdfUrl('');
    setLoading(false);
    setError('');
  }, []);

  const goToPrevious = useCallback(() => {
    if (!selectedRow || letterIndex <= 0) {
      return;
    }

    const nextIndex = letterIndex - 1;
    setLetterIndex(nextIndex);
    loadLetterAtIndex(selectedRow, nextIndex);
  }, [letterIndex, loadLetterAtIndex, selectedRow]);

  const goToNext = useCallback(() => {
    const links = selectedRow?.letter_links ?? [];
    if (!selectedRow || letterIndex >= links.length - 1) {
      return;
    }

    const nextIndex = letterIndex + 1;
    setLetterIndex(nextIndex);
    loadLetterAtIndex(selectedRow, nextIndex);
  }, [letterIndex, loadLetterAtIndex, selectedRow]);

  const letterCount = selectedRow?.letter_links?.length ?? 0;

  return {
    open,
    selectedRow,
    letterIndex,
    letterCount,
    pdfUrl,
    loading,
    error,
    openLetter,
    closeLetter,
    goToPrevious,
    goToNext,
    canGoPrevious: letterIndex > 0,
    canGoNext: letterIndex < letterCount - 1
  };
};

export default useSnapshotComparisonLetter;
